import React, { useState } from 'react';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import DialogList from 'src/dialogs';

interface ILandmanReportDialogProps {
    dialogName: string;
    hideDialog: () => void;
    showDialog: (dialogName: DialogList) => void;
}

const downloadBlobResponseAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// Ports L1099_LM.fmt's Standard screen's Report Name dropdown + Download button, mirroring
// Ap1099ReportDialog.tsx. Company (COMP_ID/COMPANY_1099) is left out entirely - COMPANY_1099 is a
// table of Leasing companies, not 1099 filers, so there's no reason to select one; company
// letterhead always comes from SCHEMA_COMPANY now, same as AP. Client Number is also left out, same
// as AP - confirmed dead in every report's own WHERE_CLAUSE construction.
const LandmanReportDialog = (props: ILandmanReportDialogProps) => {
    const { post, response } = useFetch();
    const [reportName, setReportName] = useState<string>('Landman_1099_Audit');
    const [reportTaxYear, setReportTaxYear] = useState<string>(`${new Date().getFullYear() - 1}`);
    const [reportBegNo, setReportBegNo] = useState<string>('');
    const [reportEndNo, setReportEndNo] = useState<string>('');
    const [reportNoTaxIds, setReportNoTaxIds] = useState<string>('');
    const [reportState, setReportState] = useState<string>('');
    const [generatingReport, setGeneratingReport] = useState<boolean>(false);
    const [message, setMessage] = useState<{ text: string; color?: string } | null>(null);

    const handleGenerateReport = async () => {
        if (!reportTaxYear) {
            setMessage({ text: 'Enter a Tax Year for the report.', color: 'rgba(255,0,0,0.5)' });
            return;
        }

        setGeneratingReport(true);
        await post('/v1.0/reports/lm1099', {
            reportName,
            taxYear: reportTaxYear,
            begNo: reportBegNo,
            endNo: reportEndNo,
            noTaxIds: reportNoTaxIds,
            state: reportState
        });

        if (response.ok) {
            const blob = await response.blob();
            downloadBlobResponseAsFile(blob, `${reportName}.pdf`);
        } else {
            setMessage({ text: 'Unable to generate report.', color: 'rgba(255,0,0,0.5)' });
        }
        setGeneratingReport(false);
    };

    // Returns to the 1099 Maintenance dialog rather than closing outright, matching
    // Ap1099ReportDialog.tsx's own Back button.
    const back = () => {
        props.showDialog(DialogList.LM_1099_MAINTENANCE);
    };

    return (
        <Dialog title="Landman 1099 Reports" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '30%', maxWidth: '500px' }}>
            <Panel backgroundColor="#D3D3D3">
                <Panel panelTitle="Reports">
                    <div style={{ padding: '.5rem' }}>
                        <div className="form-row" style={{ alignItems: 'center', marginBottom: '.5rem' }}>
                            <label style={{ marginRight: '.5rem', width: '7rem' }}>Report Name</label>
                            <select
                                value={reportName}
                                onChange={(e) => setReportName(e.currentTarget.value)}
                                style={{ height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem', minWidth: '14rem' }}
                            >
                                <option value="Landman_1099_NEC">Landman 1099 - NEC</option>
                                <option value="Landman_1099_Audit">Landman 1099 Audit File</option>
                                <option value="Landman_1099_ServicesExpenses">Landman 1099 Services and Expenses</option>
                            </select>
                        </div>
                        <div className="form-row" style={{ alignItems: 'center', marginBottom: '.5rem' }}>
                            <label style={{ marginRight: '.5rem', width: '7rem' }}>Tax Year</label>
                            <input
                                type="number"
                                value={reportTaxYear}
                                onChange={(e) => setReportTaxYear(e.currentTarget.value)}
                                style={{ width: '6rem', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem', marginRight: '2rem' }}
                            />
                            <label style={{ marginRight: '.5rem' }}>No Tax Ids</label>
                            <select
                                value={reportNoTaxIds}
                                onChange={(e) => setReportNoTaxIds(e.currentTarget.value)}
                                style={{ height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem' }}
                            >
                                <option value="">No</option>
                                <option value="YES">Yes</option>
                            </select>
                        </div>
                        <div className="form-row" style={{ alignItems: 'center', marginBottom: '.5rem' }}>
                            <label style={{ marginRight: '.5rem', width: '7rem' }}>Beginning No.</label>
                            <input
                                type="number"
                                value={reportBegNo}
                                onChange={(e) => setReportBegNo(e.currentTarget.value)}
                                style={{ width: '8rem', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem', marginRight: '2rem' }}
                            />
                            <label style={{ marginRight: '.5rem' }}>Ending No.</label>
                            <input
                                type="number"
                                value={reportEndNo}
                                onChange={(e) => setReportEndNo(e.currentTarget.value)}
                                style={{ width: '8rem', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem' }}
                            />
                        </div>
                        <div className="form-row" style={{ alignItems: 'center', marginBottom: '.5rem' }}>
                            <label style={{ marginRight: '.5rem', width: '7rem' }}>State</label>
                            <input
                                type="text"
                                maxLength={2}
                                value={reportState}
                                onChange={(e) => setReportState(e.currentTarget.value.toUpperCase())}
                                style={{ width: '4rem', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem' }}
                            />
                        </div>

                        {message && (
                            <div style={{ padding: '.5rem 0', color: message.color ? '#a00' : '#060' }}>{message.text}</div>
                        )}

                        <div className="form-row" style={{ justifyContent: 'center', marginTop: '1rem' }}>
                            <Button
                                text={generatingReport ? 'Generating...' : 'Download'}
                                className="blue-hover-button"
                                disabled={generatingReport}
                                style={{ width: '8rem', marginRight: '1rem' }}
                                onClick={() => handleGenerateReport()}
                            />
                            <Button
                                text="Back"
                                className="blue-hover-button"
                                disabled={generatingReport}
                                style={{ width: '8rem' }}
                                onClick={() => back()}
                            />
                        </div>
                    </div>
                </Panel>
            </Panel>
        </Dialog>
    );
};

export default LandmanReportDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/pay/LandmanReportDialog.tsx